/**
 * Girl education drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const girlEducationDrawingsPack: ToilePack = {
    slug: 'girl-education-drawings',
    category: 'coloring',
    name: 'Girl education drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'books', label: 'Girl with books', subject: 'a girl in a school uniform holding books, a pencil and the sun rising'},
        {key: 'cycle', label: 'Cycle to school', subject: 'a girl riding a bicycle to school with a bag on the carrier'},
        {key: 'graduate', label: 'Graduation', subject: 'a girl in a graduation cap holding a degree scroll'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a classroom', 'a village', 'a teacher'],
    directionHint: 'try: a classroom',
    seo: {
        title: 'Girl Education Drawings: Books, Cycle, Graduation',
        metaDescription: 'Nine girl education drawings: a girl with books, a girl cycling to school and a graduation, in pencil, colour and outline.',
        h1: 'Girl education drawings: girl with books, cycle to school, graduation',
        tagline: 'Three scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Girl education drawings for a Beti Bachao Beti Padhao poster or a school chart. The set shows a girl in a school uniform holding books, a girl riding a bicycle to school, and a girl in a graduation cap holding a degree scroll. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as a classroom, a village or a teacher.',
        useCases: [
            'a Beti Bachao Beti Padhao poster for a competition',
            "a school chart on girls' education",
            'a classroom display',
            "a card for a girl's graduation",
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Is the girl a real person?', a: 'No. She is a drawn character shown in school scenes, not a photograph of a real girl.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
